import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import ProGate from "@/components/ProGate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, RefreshCw, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fmtUsd, fmtPrice, fmtPct, CHAIN_META } from "@/lib/format";
import { PatternInsights } from "@/components/PatternInsights";
import ShareStatsButton from "@/components/ShareStatsButton";
import EdgePanel from "@/components/EdgePanel";

const fmtHeld = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h)}h`;
  const d = Math.floor(h / 24); const r = Math.round(h % 24);
  return d >= 7 ? `${d}d` : `${d}d ${r}h`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
const pnlCls = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-slate-500");
const SRC = { coingecko: "CG", dexscreener: "DEX", "dexscreener-ticker": "DEX~" };

function Tile({ label, value, sub, color = "" }) {
  return (
    <Card className="bg-[#111723] border-[#222F43] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono font-extrabold text-xl sm:text-2xl mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function StatsInner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(async (silent) => {
    silent ? setRefreshing(true) : setLoading(true);
    try { const { data } = await api.get("/trades/live"); setData(data); }
    catch { /* keep previous */ }
    finally { setLoading(false); setRefreshing(false); }
  }, []);
  useEffect(() => { load(false); const id = setInterval(() => load(true), 60000); return () => clearInterval(id); }, [load]);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 5000); return () => clearInterval(id); }, []);

  const trades = data?.trades || [];
  const open = useMemo(() => trades.filter((t) => t.status === "open"), [trades]);
  const unrealized = open.reduce((s, t) => s + (t.currentPrice ? t.pnl : 0), 0);
  const realized = trades.filter((t) => t.status === "closed").reduce((s, t) => s + t.pnl, 0);
  const openValue = open.reduce((s, t) => s + (t.currentPrice ? t.amount * (1 + t.roiPct / 100) : t.amount), 0);
  const ago = data ? Math.max(0, Math.round((Date.now() - new Date(data.updatedAt)) / 1000)) : 0;
  void tick;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5 sm:mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2"><Activity className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-400" /> Stats</h1>
          <p className="text-slate-400 text-sm mt-1">Live view of every position. Spot the patterns, fix the leaks.</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {data && <span className="hidden sm:inline text-[11px] font-mono text-slate-500" data-testid="stats-updated">updated {ago}s ago · auto 60s</span>}
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing} className="border-[#334566] bg-transparent" data-testid="stats-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ShareStatsButton disabled={loading || trades.length === 0} />
        </div>
      </div>

      {loading && <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>}

      {!loading && trades.length === 0 && (
        <Card className="bg-[#111723] border-[#222F43] p-12 text-center text-slate-400" data-testid="stats-empty">
          No trades yet. <Link to="/journal" className="text-emerald-400 hover:underline">Log your first trade</Link> or import a Solana wallet, and your live stats will show up here.
        </Card>
      )}

      {!loading && trades.length > 0 && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-summary">
            <Tile label="Unrealized P&L" value={fmtUsd(unrealized)} sub={`${open.length} open`} color={pnlCls(unrealized)} />
            <Tile label="Realized P&L" value={fmtUsd(realized)} sub={`${trades.length - open.length} closed`} color={pnlCls(realized)} />
            <Tile label="Open Value" value={fmtUsd(openValue)} sub={`${fmtUsd(open.reduce((s, t) => s + t.amount, 0))} invested`} />
            <Tile label="Total P&L" value={fmtUsd(unrealized + realized)} color={pnlCls(unrealized + realized)} />
          </div>

          <Card className="bg-[#111723] border-[#222F43] overflow-hidden mb-6">
            {/* Mobile: card rows */}
            <div className="md:hidden divide-y divide-[#222F43]" data-testid="stats-cards">
              {trades.map((t) => {
                const isOpen = t.status === "open";
                const hasLive = t.currentPrice != null;
                return (
                  <div key={t.id} className="p-4 flex items-start gap-3" data-testid={`stats-card-${t.ticker}`}>
                    <span className="text-lg leading-none mt-0.5">{t.mood}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 font-bold">
                        ${t.ticker}
                        <Badge variant="outline" className={`text-[9px] uppercase bg-transparent px-1.5 py-0 ${isOpen ? "border-cyan-500/40 text-cyan-400" : "border-slate-600 text-slate-400"}`}>{t.status}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">
                        <span style={{ color: CHAIN_META[t.chain]?.color }}>{CHAIN_META[t.chain]?.label || t.chain}</span> · {fmtPrice(t.entryPrice)} → {hasLive ? fmtPrice(t.currentPrice) : "N/A"} · held {fmtHeld(t.hoursHeld)}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        {t.marketCap ? `${fmtUsd(t.marketCap)} mcap` : ""}{t.change24h != null && hasLive ? <span className={`ml-2 ${pnlCls(t.change24h)}`}>{fmtPct(t.change24h)} 24h</span> : null}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {isOpen && !hasLive ? <span className="text-slate-600 text-xs">no price</span> : (
                        <>
                          <div className={`font-mono font-bold ${pnlCls(t.pnl)}`}>{fmtUsd(t.pnl)}</div>
                          <div className={`text-[11px] font-mono ${pnlCls(t.roiPct)}`}>{fmtPct(t.roiPct)}{isOpen && <span className="text-slate-600 ml-1">unrl</span>}</div>
                        </>
                      )}
                      <div className="text-[10px] text-slate-600 font-mono mt-0.5">{fmtUsd(t.amount)} in</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#222F43] hover:bg-transparent">
                    <TableHead>Coin</TableHead>
                    <TableHead className="text-right">Entry</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead className="text-right">Mkt Cap</TableHead>
                    <TableHead className="text-right">24h</TableHead>
                    <TableHead className="text-right">24h Vol</TableHead>
                    <TableHead className="text-right">Held</TableHead>
                    <TableHead className="text-right">P&L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trades.map((t) => {
                    const isOpen = t.status === "open";
                    const hasLive = t.currentPrice != null;
                    return (
                      <TableRow key={t.id} className="border-[#222F43]" data-testid={`stats-row-${t.ticker}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-base">{t.mood}</span>
                            <div className="min-w-0">
                              <div className="font-bold flex items-center gap-1.5">
                                ${t.ticker}
                                <Badge variant="outline" className={`text-[9px] uppercase bg-transparent px-1.5 py-0 ${isOpen ? "border-cyan-500/40 text-cyan-400" : "border-slate-600 text-slate-400"}`}>{t.status}</Badge>
                              </div>
                              <div className="text-[11px] text-slate-500 truncate max-w-[160px]">
                                <span style={{ color: CHAIN_META[t.chain]?.color }}>{CHAIN_META[t.chain]?.label || t.chain}</span>
                                {t.name && <> · {t.name}</>}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="font-mono text-xs">{fmtPrice(t.entryPrice)}</div>
                          <div className="text-[11px] text-slate-500">{fmtDate(t.entryDate)} · {fmtUsd(t.amount)}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {hasLive ? fmtPrice(t.currentPrice) : <span className="text-slate-600">N/A</span>}
                          {t.source && <span className="ml-1 text-[9px] text-slate-600" title={t.source}>{SRC[t.source]}</span>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{t.marketCap ? fmtUsd(t.marketCap) : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell className={`text-right font-mono text-xs ${pnlCls(t.change24h)}`}>{t.change24h != null && hasLive ? fmtPct(t.change24h) : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{t.volume24h ? fmtUsd(t.volume24h) : <span className="text-slate-600">—</span>}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-300">{fmtHeld(t.hoursHeld)}</TableCell>
                        <TableCell className="text-right">
                          {isOpen && !hasLive ? <span className="text-slate-600 text-xs">no price</span> : (
                            <>
                              <div className={`font-mono font-bold ${pnlCls(t.pnl)}`}>{fmtUsd(t.pnl)}</div>
                              <div className={`text-[11px] font-mono ${pnlCls(t.roiPct)}`}>{fmtPct(t.roiPct)}{isOpen && <span className="text-slate-600 ml-1">unrl</span>}</div>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="px-4 py-2 text-[10px] text-slate-600 font-mono border-t border-[#222F43]">
              CG = CoinGecko · DEX = DexScreener · DEX~ = matched by ticker (add the contract address in Journal for an exact match)
            </div>
          </Card>

          <PatternInsights trades={trades} />
          <EdgePanel refreshKey={data?.updatedAt} />
        </>
      )}
    </div>
  );
}

export default function Stats() {
  return <ProGate feature="Stats"><StatsInner /></ProGate>;
}
